import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Skeleton from "@mui/material/Skeleton";

interface Props {
	count?: number;
}

interface EmailContentReportSkeletonProps {
	metricsCount?: number;
}

export function UsageReportCardSkeleton({ count = 3 }: Props) {
	return (
		<Box display="flex" flexDirection="column" gap={3} marginTop={3}>
			{Array.from({ length: count }).map((_, index) => (
				<Card
					key={index}
					sx={{
						padding: "15px",
						borderRadius: "6px",
						borderWidth: "0.6px",
						borderStyle: "solid",
						borderColor: "#D9D9F7",
						opacity: 1,
						boxShadow: "none",
						background: "linear-gradient(96.04deg, #FFFFFF 1.99%, #F2F1FF 93.82%, #F4EFFF 115.69%)",
					}}
				>
					<CardContent sx={{ padding: "24px" }}>
						<Box display="flex" flexDirection={{ xs: "column", lg: "row" }} gap={3}>
							{/* LEFT SECTION */}
							<Box
								sx={{
									flex: { xs: "0 0 auto", lg: "0 0 200px" },
									alignSelf: { xs: "center", lg: "stretch" },
									borderRadius: 2,
									padding: "15px 65px",
									textAlign: "center",
								}}
							>
								<Skeleton variant="text" width={120} height={32} animation="wave" sx={{ marginBottom: "4px" }} />
								<Skeleton variant="text" width={100} height={24} animation="wave" />
							</Box>

							{/* RIGHT SECTION */}
							<Box flex="1" display="flex" flexDirection="column" gap={2}>
								<Box display="flex" alignItems="center" marginBottom="8px">
									<Skeleton variant="text" width={300} height={28} animation="wave" />
									{index === 0 && (
										<Skeleton variant="rounded" width={80} height={20} animation="wave" sx={{ marginLeft: "16px" }} />
									)}
								</Box>
								<Skeleton variant="text" width="80%" height={20} animation="wave" sx={{ marginBottom: "8px" }} />
								<Skeleton variant="text" width="60%" height={20} animation="wave" sx={{ marginBottom: "16px" }} />
								<Skeleton variant="rounded" width={120} height={36} animation="wave" />
							</Box>
						</Box>
					</CardContent>
				</Card>
			))}
		</Box>
	);
}

export function EmailContentReportSkeleton({ metricsCount = 5 }: EmailContentReportSkeletonProps) {
	return (
		<Box
			sx={{
				width: "100%",
				minHeight: "100vh",
				background: `linear-gradient(180deg, rgba(253, 93, 239, 0.1) 0%, rgba(253, 93, 239, 0.1) 50%, rgba(94, 195, 255, 0.1) 100%)`,
				backgroundColor: "#FCFCFC",
				paddingTop: 4,
				paddingBottom: 4,
			}}
			display="flex"
			flexDirection="column"
			alignItems="center"
			gap={4}
		>
			{/* Header: Logo + Date + Banner */}
			<Box display="flex" flexDirection="column" alignItems="center" gap={2}>
				<Box paddingBottom={5}>
					<Skeleton variant="rectangular" width={90} height={40} />
				</Box>
				<Box width="100%" display="flex" justifyContent="center">
					<Skeleton variant="text" width={180} height={28} />
				</Box>
				<Skeleton variant="rectangular" width={600} height={100} />
			</Box>

			{/* Main card */}
			<Box
				sx={{
					border: "1px solid #f2f2f2",
					borderRadius: 2,
					backgroundColor: "#ffffff",
					padding: 3,
					maxWidth: 900,
					width: "100%",
				}}
				display="flex"
				flexDirection="column"
				alignItems="center"
				gap={2}
			>
				<Skeleton variant="text" width={200} height={20} />
				<Skeleton variant="text" width="60%" height={40} />
				<Skeleton variant="text" width="90%" height={60} />
			</Box>

			{/* Metrics cards */}
			<Box display="flex" flexDirection="column" gap={3} width="100%">
				{Array.from({ length: metricsCount }).map((_, index) => (
					<Box
						key={index}
						sx={{
							border: "1px solid #ececec",
							borderRadius: 2,
							padding: 3,
							backgroundColor: "#f9f9f9",
							maxWidth: 900,
							width: "100%",
							margin: "0 auto",
							height: 120,
						}}
						display="flex"
						gap={2}
					>
						{/* Left section: title + number (30% width) */}
						<Box display="flex" flexDirection="column" gap={1} width="30%">
							<Skeleton variant="text" width="80%" height={28} /> {/* title */}
							<Skeleton variant="text" width="60%" height={50} /> {/* big number */}
						</Box>

						{/* Right section: title + description (70% width) */}
						<Box display="flex" flexDirection="column" gap={1} width="70%">
							<Skeleton variant="text" width="70%" height={28} /> {/* right title */}
							<Skeleton variant="text" width="90%" height={40} /> {/* right description */}
						</Box>
					</Box>
				))}
			</Box>

			{/* Footer: social icons + copyright */}
			<Box display="flex" flexDirection="column" alignItems="center" gap={2} marginTop={4}>
				<Box display="flex" gap={5}>
					{Array.from({ length: 6 }).map((_, index) => (
						<Skeleton key={index} variant="rectangular" width={30} height={30} />
					))}
				</Box>
				<Skeleton variant="text" width="60%" height={24} />
				<Skeleton variant="rectangular" width={135} height={40} />
			</Box>
		</Box>
	);
}
